import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type SavingsFormType, getSavingsFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/savingsFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ContractProposalBaseInvestmentToolType,
  CreateContractProposalSavingBodyInvestmentToolType,
  PatchContractProposalSavingBodyInvestmentToolType,
} from "Infrastructure/ApiZfp/Schema";
import type {
  ContractProposalBaseSolutionHorizonType,
  CreateContractProposalSavingBody,
  PatchContractProposalSavingBody,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailSaving } from "Infrastructure/ApiZfp/Schema/contractProposalDetailSaving";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";

type Props = {
  readonly edit?: boolean;
  readonly partner?: string;
  readonly proposedSolutionId?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly initialData?: ContractProposalDetailSaving | null;
};

export const SavingsForm = forwardRef<{}, Props>(({ partner, edit, proposedSolutionId, horizonType, onSuccess, initialData }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();

  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const methods = useForm<SavingsFormType>({
    resolver: yupResolver(getSavingsFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: initialData.investmentToolType,
            partner: partner ?? "",
            oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
            regularAmount: initialData.regularAmount?.value ?? 0,
            horizon: initialData.currentSolutionHorizon ?? 0,
            appreciationRate: initialData.appreciationRate ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            stateContribution: initialData.savingDetail?.stateContribution?.value ?? 0,
            note: initialData.note ?? "",
          }
        : {
            investmentToolType: ContractProposalBaseInvestmentToolType.saving,
            partner: partner ?? "",
          },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (edit && initialData) {
      reset({
        investmentToolType: initialData.investmentToolType,
        partner: partner ?? "",
        oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
        regularAmount: initialData.regularAmount?.value ?? 0,
        horizon: initialData.currentSolutionHorizon ?? 0,
        appreciationRate: initialData.appreciationRate ?? 0,
        equities: initialData.assetAllocation?.equities ?? 0,
        bonds: initialData.assetAllocation?.bonds ?? 0,
        commodities: initialData.assetAllocation?.commodities ?? 0,
        realEstate: initialData.assetAllocation?.realEstate ?? 0,
        cash: initialData.assetAllocation?.cash ?? 0,
        other: initialData.assetAllocation?.others ?? 0,
        stateContribution: initialData.savingDetail?.stateContribution?.value ?? 0,
        note: initialData.note ?? "",
      });
    }
  }, [edit, initialData, reset, partner]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (edit) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
    triggerValidation: () => {
      trigger();
    },
  }));

  const onCreate = (data: SavingsFormType) => {
    if (!horizonType) return;

    const payload: CreateContractProposalSavingBody = {
      investmentToolType: CreateContractProposalSavingBodyInvestmentToolType.saving,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      note: data.note ?? "",
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: {
        value: data.regularAmount ?? 0,
        currencyCode: "CZK",
        frequency: AmountFrequency.monthly,
      },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      savingDetail: {
        stateContribution: { value: data.stateContribution ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      },
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  const onEdit = (data: SavingsFormType) => {
    if (!proposedSolutionId) return;

    const payload: PatchContractProposalSavingBody = {
      investmentToolType: PatchContractProposalSavingBodyInvestmentToolType.saving,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      note: data.note ?? "",
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: {
        value: data.regularAmount ?? 0,
        currencyCode: "CZK",
        frequency: AmountFrequency.monthly,
      },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      savingDetail: {
        stateContribution: { value: data.stateContribution ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      },
    };

    updateMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  name="oneTimeDeposit"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeDeposit)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  name="regularAmount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  inputEndAdornment={
                    <Box sx={{ width: 110 }}>
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="horizon"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.Years)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="appreciationRate"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      %
                    </Typography>
                  }
                  labelEndAdornment={<StyledLabelAdornment />}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                <BlFormInput
                  control={control}
                  name="stateContribution"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.StateContribution)}
                  errors={errors}
                  inputEndAdornment={
                    <Box sx={{ width: 110 }}>
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Box>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <CharacteristicsAccordion<SavingsFormType>
              key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
              defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
            />
          </Box>

          <Divider />

          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                  placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
